/**
 * "My inventions" — the Watch view (the figure's sixth tab).
 *
 * What you have asked to be told about, and what has moved since:
 *   GET    /whitespace/portfolio_watch?scope=own     (your own watch rows)
 *   DELETE /whitespace/portfolio_watch/{id}?scope=own
 *   GET    /whitespace/portfolio_pressure?scope=own  (who filed near them)
 *   GET    /whitespace/portfolio_brief?scope=own     (the whole thing as .docx)
 *
 * Own and aggregator watch rows share ONE table, and the list route returns
 * both to either caller — the client is what keeps the two views apart. This
 * view shows OWN rows only, through portfolioWatchesForAggregator(rows, null),
 * so a portfolio's watch can never appear on a personal screen.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileDown, Loader2, Trash2 } from 'lucide-react'
import {
  listPortfolioWatches,
  deletePortfolioWatch,
  getPortfolioPressure,
  getPortfolioBrief,
} from '../../services/api'
import { portfolioWatchesForAggregator } from '../../lib/whitespace'
import { downloadBase64File } from '../whitespace/PortfolioWatchPanel'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import { OWN_SCOPE_ENTITLEMENT_LINE } from './ownScopeViews'
import type { Project } from '../../types'

const OWN = 'own' as const

export interface OwnWatchViewProps {
  owned: Project[]
}

export default function OwnWatchView({ owned }: OwnWatchViewProps) {
  const queryClient = useQueryClient()
  const [notice, setNotice] = useState<string | null>(null)

  const watchesQuery = useQuery({
    queryKey: ['portfolio-watches', 'own'],
    queryFn: () => listPortfolioWatches(OWN),
    retry: false,
  })
  const pressureQuery = useQuery({
    queryKey: ['portfolio-pressure', 'own'],
    queryFn: () => getPortfolioPressure(null, OWN),
    retry: false,
  })

  const names = useMemo(() => new Map(owned.map((p) => [p.project_id, p.name])), [owned])
  const watches = useMemo(
    () => portfolioWatchesForAggregator(watchesQuery.data ?? [], null),
    [watchesQuery.data],
  )

  const remove = useMutation({
    mutationFn: (watchId: string) => deletePortfolioWatch(watchId, OWN),
    onSuccess: () => {
      setNotice('That watch has been removed. Nothing else changed.')
      void queryClient.invalidateQueries({ queryKey: ['portfolio-watches', 'own'] })
    },
    onError: (e) => setNotice((e as Error)?.message ?? 'That watch could not be removed.'),
  })

  const brief = useMutation({
    mutationFn: () => getPortfolioBrief(null, OWN),
    onSuccess: (b) => {
      downloadBase64File(b.docx_base64, b.filename, b.content_type)
      setNotice(`Downloaded ${b.filename}.`)
    },
    onError: (e) => setNotice((e as Error)?.message ?? 'The brief could not be assembled.'),
  })

  if (watchesQuery.isLoading) return <LoadingSkeleton type="card" rows={3} />

  return (
    <div data-part="OwnWatchView" className="space-y-3">
      <div className="flex items-start gap-2.5 rounded-lg border border-iflow-200 bg-iflow-50/70 px-3 py-2.5 text-[12px] leading-relaxed text-iflow-900">
        <span aria-hidden className="mt-0.5 font-bold text-iflow-600">ℹ</span>
        <p>
          <b>What you asked to be told about.</b> A watch is a band of the corpus around a region of
          your set; the rescan counts who has filed into it since. Start one from a region on Shape
          &amp; Strategy — this view is where they live afterwards.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[12px] text-gray-600">
          {watches.length === 0
            ? 'You are watching nothing yet'
            : `${watches.length} watch${watches.length === 1 ? '' : 'es'} of your own`}{' '}
          · <b className="font-semibold text-iflow-800">{OWN_SCOPE_ENTITLEMENT_LINE}</b>
        </p>
        <button
          type="button"
          onClick={() => brief.mutate()}
          disabled={brief.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-[12px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {brief.isPending ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
          {brief.isPending ? 'Assembling…' : 'Download the whole reading (.docx)'}
        </button>
      </div>

      {watchesQuery.isError ? (
        <p role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-[12.5px] text-amber-900">
          We could not load your watches. Nothing has been removed — the list simply did not come
          back.
        </p>
      ) : watches.length === 0 ? (
        <p className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-[12.5px] text-gray-600">
          Nothing is being watched yet. Open Shape &amp; Strategy, pick a region worth following,
          and it appears here.
        </p>
      ) : (
        <ul className="space-y-2.5">
          {watches.map((w) => (
            <li key={w.watch_id} className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3.5">
              <div className="min-w-0">
                <p className="text-[13px] font-bold text-gray-900">
                  {w.corpus} · band {w.band[0]}–{w.band[1]}
                </p>
                <p className="mt-0.5 text-[11.5px] text-gray-500">
                  Across {w.member_project_ids.map((id) => names.get(id) ?? id).join(', ')} ·{' '}
                  {w.record_ids.length} document{w.record_ids.length === 1 ? '' : 's'} on its boundary
                  {typeof w.wos_score === 'number' && <> · scored {w.wos_score}</>}
                </p>
              </div>
              <button
                type="button"
                onClick={() => remove.mutate(w.watch_id)}
                disabled={remove.isPending}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1 text-[11.5px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Trash2 size={13} /> Stop watching
              </button>
            </li>
          ))}
        </ul>
      )}

      <section aria-label="Encroachment pressure" className="rounded-xl border border-gray-200 bg-white p-3.5">
        <h3 className="text-[13px] font-bold text-gray-900">Who has filed nearby</h3>
        {pressureQuery.isLoading ? (
          <p className="mt-1 text-[11.5px] text-gray-500">Reading the last rescan…</p>
        ) : pressureQuery.isError ? (
          <p className="mt-1 text-[11.5px] text-gray-500">
            The pressure counters did not come back, so nothing is claimed about who has filed
            nearby.
          </p>
        ) : (pressureQuery.data?.competitors ?? []).length === 0 ? (
          <p className="mt-1 text-[11.5px] text-gray-500">
            No filer has moved into the bands you watch in the last{' '}
            {pressureQuery.data?.window_days ?? 90} days. That is a measured zero, not a missing
            number.
          </p>
        ) : (
          <ul className="mt-1.5 divide-y divide-gray-100">
            {(pressureQuery.data?.competitors ?? []).map((c) => (
              <li key={c.competitor_norm} className="flex items-center justify-between gap-3 py-1.5 text-[12px]">
                <span className="min-w-0 flex-1 truncate text-gray-900">{c.display_name}</span>
                <span className="shrink-0 text-gray-500">
                  {c.pressure_count} filing{c.pressure_count === 1 ? '' : 's'} in the last{' '}
                  {pressureQuery.data?.window_days ?? 90} days
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {notice && (
        <p role="status" className="text-[11px] text-gray-600">
          {notice}
        </p>
      )}
    </div>
  )
}
